// Campaign List Component
import React, { useState, useEffect } from 'react';
import { useCampaign } from '../../context/CampaignContext';
import { useAuth } from '../../context/AuthContext';
import { CAMPAIGN_TYPES, CAMPAIGN_STATUS, getCampaignTypeName, getCampaignStatusName } from '../../data/campaignModels';
import { getCampaignDaysRemaining } from '../../data/campaignModels';

const CampaignList = ({ onEdit, onView, onApprove, onReject, onModerate, onPayment, onDelete, onToggleActive }) => {
  const { campaigns, loading, loadCampaigns, updateCampaign, deleteCampaign } = useCampaign();
  const { currentUser } = useAuth();
  const [filter, setFilter] = useState('all'); // all, draft, pending-approval, approved, active, expired
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh key

  useEffect(() => {
    loadCampaigns({ status: filter !== 'all' ? filter : null, type: typeFilter !== 'all' ? typeFilter : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, typeFilter, refreshKey]);

  const filteredCampaigns = campaigns.filter(campaign => {
    // Status filter
    if (filter !== 'all' && campaign.status !== filter) return false;
    
    // Type filter
    if (typeFilter !== 'all' && campaign.type !== typeFilter) return false;
    
    // Search filter
    if (searchTerm) {
      const name = campaign.name?.th || campaign.name?.en || '';
      const searchLower = searchTerm.toLowerCase();
      if (!name.toLowerCase().includes(searchLower)) return false;
    }
    
    return true;
  });

  const paginatedCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      'pending-approval': 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-green-100 text-green-800',
      paused: 'bg-orange-100 text-orange-800',
      expired: 'bg-gray-100 text-gray-600'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {getCampaignStatusName(status, 'th')}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      promotion: 'bg-purple-100 text-purple-800',
      'banner-rental': 'bg-blue-100 text-blue-800',
      seasonal: 'bg-green-100 text-green-800',
      announcement: 'bg-orange-100 text-orange-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100'}`}>
        {getCampaignTypeName(type, 'th')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-2 text-gray-500">กำลังโหลด...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="mb-6 space-y-4">
        {/* Search */}
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="ค้นหา Campaign..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          {['all', 'draft', 'pending-approval', 'approved', 'active', 'expired'].map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {f === 'all' ? 'ทั้งหมด' : getCampaignStatusName(f, 'th')}
            </button>
          ))}
        </div>

        {/* Type Filters */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-600 self-center">ประเภท:</span>
          {['all', ...Object.values(CAMPAIGN_TYPES)].map((t) => (
            <button
              key={t}
              onClick={() => {
                setTypeFilter(t);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === t
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t === 'all' ? 'ทั้งหมด' : getCampaignTypeName(t, 'th')}
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns Table */}
      {filteredCampaigns.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-gray-400 text-4xl mb-2">📢</div>
          <p className="text-gray-500">ไม่พบ Campaign</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">แสดง</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่เริ่ม</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่สิ้นสุด</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Analytics</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จัดการ</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCampaigns.map((campaign) => {
                  const daysRemaining = getCampaignDaysRemaining(campaign);
                  const startDate = campaign.startDate?.toDate?.() || new Date(campaign.startDate);
                  const endDate = campaign.endDate?.toDate?.() || new Date(campaign.endDate);
                  
                  return (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {campaign.name?.th || campaign.name?.en || 'ไม่มีชื่อ'}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {campaign.id?.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(campaign.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(campaign.status)}
                        {daysRemaining !== null && campaign.status === 'active' && (
                          <div className="text-xs text-gray-500 mt-1">
                            เหลือ {daysRemaining} วัน
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={async () => {
                            try {
                              await updateCampaign(campaign.id, {
                                isActive: !(campaign.isActive !== false)
                              });
                              await loadCampaigns({});
                            } catch (error) {
                              console.error('Error toggling campaign active:', error);
                              alert('เกิดข้อผิดพลาด: ' + error.message);
                            }
                          }}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            campaign.isActive !== false
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={campaign.isActive !== false ? 'คลิกเพื่อปิดการแสดง' : 'คลิกเพื่อเปิดการแสดง'}
                        >
                          {campaign.isActive !== false ? '✅ เปิด' : '❌ ปิด'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {startDate.toLocaleDateString('th-TH')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {endDate ? endDate.toLocaleDateString('th-TH') : 'ไม่จำกัด'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="text-gray-600">
                          👁️ {campaign.analytics?.views || 0}
                        </div>
                        <div className="text-gray-600">
                          👆 {campaign.analytics?.clicks || 0}
                        </div>
                        {campaign.analytics?.views > 0 && (
                          <div className="text-xs text-gray-500">
                            CTR: {((campaign.analytics?.clicks || 0) / campaign.analytics.views * 100).toFixed(2)}%
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => onView(campaign)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            ดู
                          </button>
                          {campaign.status === 'draft' && (
                            <button
                              onClick={() => onEdit(campaign)}
                              className="text-green-600 hover:text-green-900"
                            >
                              แก้ไข
                            </button>
                          )}
                          {campaign.status === 'pending-approval' && (
                            <>
                              <button
                                onClick={() => onApprove(campaign)}
                                className="text-green-600 hover:text-green-900"
                              >
                                อนุมัติ
                              </button>
                              <button
                                onClick={() => onReject(campaign)}
                                className="text-red-600 hover:text-red-900"
                              >
                                ปฏิเสธ
                              </button>
                            </>
                          )}
                          {campaign.moderation?.status === 'pending' && (
                            <button
                              onClick={() => onModerate(campaign)}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              ตรวจสอบ
                            </button>
                          )}
                          {campaign.type === 'banner-rental' && campaign.rental?.paymentStatus === 'pending' && (
                            <button
                              onClick={() => onPayment(campaign)}
                              className="text-green-600 hover:text-green-900"
                            >
                              ชำระเงิน
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              const campaignName = campaign.name?.th || campaign.name?.en || 'Campaign';
                              if (window.confirm(`ยืนยันการลบ Campaign "${campaignName}"?\n\nการลบนี้ไม่สามารถยกเลิกได้!`)) {
                                if (window.confirm(`⚠️ ยืนยันอีกครั้ง!\n\nคุณแน่ใจหรือไม่ว่าต้องการลบ Campaign "${campaignName}"?\n\nการลบนี้ไม่สามารถยกเลิกได้!`)) {
                                  try {
                                    await deleteCampaign(campaign.id);
                                    alert('ลบ Campaign สำเร็จ');
                                    await loadCampaigns({});
                                  } catch (error) {
                                    console.error('Error deleting campaign:', error);
                                    alert('เกิดข้อผิดพลาด: ' + error.message);
                                  }
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="ลบ Campaign"
                          >
                            ลบ
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                แสดง {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredCampaigns.length)} จาก {filteredCampaigns.length} รายการ
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ก่อนหน้า
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  หน้า {currentPage} จาก {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ถัดไป
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignList;

